import { alpha, Grid, Typography, useTheme } from "@mui/material";
import { isFulfilled } from "@reduxjs/toolkit";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../../../app/hooks";
import useCustomerOrgsAndWorkLocationsAutocomplete from "../../../../../../common/components/functional/autocomplete/customerOrgsAndWorkLocationsAutocomplete/useCustomerOrgsAndWorkLocationsAutocomplete";
import DialogComponent from "../../../../../../common/components/functional/dialogs/DialogComponent";
import DialogContentWrapper from "../../../../../../common/components/functional/dialogs/DialogContentWrapper";
import OverlaySpinner from "../../../../../../common/components/functional/overlaySpinner/OverlaySpinner";
import isEmpty from "../../../../../../common/helpers/isEmpty";
import {
  introduceToCustomerWithoutOrder,
  selectTalentById,
} from "../../../../../talents/talentsSlice";
import { TalentIntroducedToCustomerStatus } from "../../../../../talents/types/queries";
import { fetchWorkTitles } from "../../../../../workTitles/workTitleSlice";
import { TalentIntroductionRow } from "../../../types";
import CustomerSection from "./CustomerSection";
import TalentWorkTitleSelector from "./TalentWorkTitleSelector";
import useExclusivityConflict from "./useExclusivityConflict";

type Props = {
  open: boolean;
  toggleDialog: () => void;
  updateLocalIntroductions: (
    newIntroduction: TalentIntroductionRow,
    index: number,
  ) => void;
  candidateIntroductions: TalentIntroductionRow[];
};

const TalentIntroduceToCustomerDialog = ({
  open,
  toggleDialog,
  updateLocalIntroductions,
  candidateIntroductions,
}: Props) => {
  const [removedWorkTitles, setRemovedWorkTitles] = useState<string[]>([]);
  const [isExistingIntroduction, setIsExistingIntroduction] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [activeWorkTitleIds, setActiveWorkTitleIds] = useState<string[] | null>(
    null,
  );

  const { t, i18n } = useTranslation("talentProfile");
  const theme = useTheme();
  const dispatch = useAppDispatch();

  const { talentId } = useParams();
  const talent = useAppSelector((state) => selectTalentById(state, talentId!!));

  const {
    workLocation,
    onChange,
    workLocationOptions,
    onInputChange,
    isLoading: isWorkLocationsLoading,
    reset,
  } = useCustomerOrgsAndWorkLocationsAutocomplete();

  const introducibleWorkTitles = useMemo(() => {
    const talentWorkTitles = talent.workTitles?.values || [];
    if (!activeWorkTitleIds) return talentWorkTitles;
    const activeIds = new Set(activeWorkTitleIds);
    return talentWorkTitles.filter((workTitle) => activeIds.has(workTitle.id));
  }, [talent.workTitles?.values, activeWorkTitleIds]);

  const getSelectedWorkTitles = useCallback(
    () =>
      introducibleWorkTitles.filter(
        (workTitle) => !removedWorkTitles.includes(workTitle.id),
      ),
    [introducibleWorkTitles, removedWorkTitles],
  );

  const selectedWorkTitles = useMemo(
    () => getSelectedWorkTitles(),
    [getSelectedWorkTitles],
  );
  const selectedWorkTitleIds = useMemo(
    () => selectedWorkTitles.map((workTitle) => workTitle.id),
    [selectedWorkTitles],
  );

  useEffect(() => {
    if (!open) return;
    dispatch(fetchWorkTitles({ language: i18n.language.substring(0, 2) })).then(
      (action) => {
        if (!isFulfilled(action)) return;
        setActiveWorkTitleIds(action.payload.map((workTitle) => workTitle.id));
      },
    );
  }, [open, dispatch, i18n.language]);

  const {
    isExclusivityConflict,
    isValidatingExclusivity,
    isExclusivityCheckFailed,
  } = useExclusivityConflict(talent.id, workLocation, selectedWorkTitleIds);

  useEffect(() => {
    const isExisting = candidateIntroductions.find((introduction) => {
      const isSameWorkLocation =
        introduction.workLocationId === workLocation?.id;

      const isSameWorkTitles =
        introduction.workTitles.length === selectedWorkTitles.length &&
        selectedWorkTitles.every((selectedWorkTitle) =>
          introduction.workTitles.some(
            (workTitle) => workTitle.id === selectedWorkTitle.id,
          ),
        );
      return isSameWorkLocation && isSameWorkTitles;
    });

    setIsExistingIntroduction(!!isExisting);
  }, [workLocation, selectedWorkTitles, candidateIntroductions]);

  const onClose = () => {
    toggleDialog();
    setRemovedWorkTitles([]);
    setActiveWorkTitleIds(null);
    reset();
  };

  const onSave = () => {
    const existingIndex = candidateIntroductions.findIndex(
      (candidateIntroduction) =>
        candidateIntroduction.workLocationId === workLocation?.id,
    );

    if (workLocation?.id && !isEmpty(selectedWorkTitles)) {
      setIsLoading(true);

      const payload = {
        workLocationId: workLocation.id,
        talents: [
          {
            id: talent.id,
            workTitleIds: selectedWorkTitles.map((workTitle) => workTitle.id),
          },
        ],
      };

      dispatch(introduceToCustomerWithoutOrder(payload))
        .then((action) => {
          if (isFulfilled(action)) {
            const newIntroduction = {
              customerOrgId: workLocation.customerOrgId,
              // typed as optional from backend
              customerOrgName: workLocation?.customerOrgName || "",
              workLocationId: workLocation.id,
              workLocationName: workLocation.name,
              talentId: talent.id,
              introducedAt: Date.now(),
              workTitles: selectedWorkTitles,
              status: TalentIntroducedToCustomerStatus.Introduced,
              bundleId: null,
            };

            updateLocalIntroductions(newIntroduction, existingIndex);
          }
        })
        .finally(() => {
          setIsLoading(false);
          onClose();
        });
    }
  };

  const getBgColors = (id: string) => {
    const primaryLight = theme.palette.primary.light;
    const isRemoved = removedWorkTitles.includes(id);

    const color = isRemoved ? alpha(primaryLight, 0.38) : primaryLight;
    // show hover effect only on removed work titles
    const hoverColor = isRemoved ? alpha(primaryLight, 0.28) : color;

    return {
      backgroundColor: color,
      "&:hover": { backgroundColor: hoverColor },
    };
  };

  const removeWorkTitle = (id: string) => {
    if (!removedWorkTitles.includes(id)) {
      setRemovedWorkTitles((prevState) => [...prevState, id]);
    }
  };

  const selectWorkTitle = (id: string) =>
    setRemovedWorkTitles((prevState) =>
      prevState.filter((workTitleId) => workTitleId !== id),
    );

  const allWorkTitlesRemoved =
    introducibleWorkTitles.length > 0 &&
    removedWorkTitles.length === introducibleWorkTitles.length;

  const validationErrors = [
    isExistingIntroduction && t("profileSummary.existingIntroductionError"),
    isExclusivityConflict && t("profileSummary.exclusivityConflictError"),
  ].filter(Boolean) as string[];

  const saveDisabled =
    !workLocation?.id ||
    isEmpty(introducibleWorkTitles) ||
    allWorkTitlesRemoved ||
    isLoading ||
    isValidatingExclusivity ||
    isExclusivityCheckFailed ||
    validationErrors.length > 0;

  return (
    <DialogComponent open={open} onClose={onClose} variant="form" autoWidth>
      <DialogContentWrapper
        title={t("profileSummary.introduceTalentToCustomer")}
        onCancel={onClose}
        onSave={onSave}
        saveDisabled={saveDisabled}
        containerSx={{ width: "532px" }}
      >
        <Grid container item spacing={2}>
          <OverlaySpinner isLoading={isLoading} />
          <CustomerSection
            options={workLocationOptions}
            onChange={onChange}
            onInputChange={(value, reason) => onInputChange(value, reason)}
            value={workLocation}
            isLoading={isWorkLocationsLoading}
          />
          <TalentWorkTitleSelector
            title={`${t("profileSummary.introduceTalentToCustomer")}:`}
            talent={talent}
            chipListProps={{
              list: introducibleWorkTitles,
              idKey: "id",
              translationKey: "workTitle",
              emptyState: t("profileSummary.emptyWorkTitles"),
              onDelete: (id) => removeWorkTitle(id as string),
              onClick: (id) => selectWorkTitle(id as string),
              getChipStyles: (id) => getBgColors(id as string),
            }}
          />
          {validationErrors.length > 0 && (
            <Grid item xs={12}>
              {validationErrors.map((error) => (
                <Typography key={error} mt={1} color="error.main">
                  {error}
                </Typography>
              ))}
            </Grid>
          )}
        </Grid>
      </DialogContentWrapper>
    </DialogComponent>
  );
};

export default TalentIntroduceToCustomerDialog;
